import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import Icon from '../../../../atoms/Icon'
import { useHideIfNoDataWhen } from '../../../HideIfNoData'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import Paging from '../HierarchicalOpalGrid/Paging'
import Screen from '../HierarchicalOpalGrid/Screen'
import { useDDPReportIndexedDBContext } from './DDPReportIndexedDBContext'

const mapAccessorKey = column => column.accessorKey ? column.accessorKey : column.accessor
const mapSize = column => column.size ? column.size : column.width
const mapMaxSize = column => column.maxSize ? column.maxSize : column.maxWidth
const mapMinSize = column => column.minSize ? column.minSize : column.minWidth
const mapHeader = column => column.header ? column.header : column.Header

export const useColumns = (columns, totals) => {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    const includeFooter = totals?.length > 0
    const templatedColumns = columns.map((column) => {
      const result = {
        ...column,
        header: mapHeader(column),
        accessorKey: mapAccessorKey(column),
        size: mapSize(column),
        maxSize: mapMaxSize(column),
        minSize: mapMinSize(column)
      }
      if (!result?.cell) {
        result.cell = (props) => formatter(props.getValue(), column.format)
      }
      if (includeFooter && !result?.footer) {
        const total = totals.at(0)
        result.footer = () => formatter(total[mapAccessorKey(column)], column.format)
      }

      return result
    })

    return {
      columns: templatedColumns,
      includeFooter
    }
  }, [formatter, columns, totals])
}

function mapHeaderClasses (defaults, header, other = '') {
  const col = header?.column?.columnDef ?? {}
  const sortable = header.column.getCanSort()
  return clsx(defaults, col.headerClasses, other, {
    clickable: sortable,
    'select-none': sortable
  })
}

function mapHeaderLabelClasses (defaults, header) {
  const col = header?.column?.columnDef ?? {}
  return clsx('flex flex-row items-center', defaults, {
    'justify-center': col.align === 'center',
    'justify-start': !col.align || col.align === 'left',
    'justify-end': col.align === 'right'
  })
}

function mapCellClasses (defaults, cell, other = '') {
  const col = cell?.column?.columnDef ?? {}
  return clsx(defaults, col.cellClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapFooterClasses (defaults, header, other = '') {
  const col = header?.column?.columnDef ?? {}
  return clsx(defaults, col.footerClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapSortIcon (header) {
  const sortDir = header.column.getIsSorted()
  if (sortDir === 'asc') return 'up'
  if (sortDir === 'desc') return 'down'
  return ''
}

function mapCellStyles (defaultStyles, cell) {
  return {
    ...(defaultStyles || {}),
    width: cell.column.getSize()
  }
}

function mapHeaderStyles (defaultStyles, header) {
  return {
    ...(defaultStyles || {}),
    width: header.column.getSize()
  }
}

/**
 * Grid component for IndexedDB-backed data
 * Uses server-side pagination pattern but queries IndexedDB
 * Only loads current page into memory (~1-10MB vs 800MB)
 */
function DDPReportIndexedDBGrid ({
  defaultSort,
  pageSizeOptions,
  fixedHeight,
  className,
  style,
  componentClasses,
  componentStyles
}) {
  const {
    data,
    totalRows,
    totals,
    isLoading,
    isFetching,
    pageIndex,
    pageSize,
    goToPage,
    changePageSize,
    sortBy,
    sortDesc,
    changeSorting,
    columnConfig // Get columnConfig from context
  } = useDDPReportIndexedDBContext()

  const { columns, includeFooter } = useColumns(columnConfig, totals)

  // Used with <HideIfNoData />
  useHideIfNoDataWhen(() => !isLoading && !data?.length, [data, isLoading])

  // Build sorting state for TanStack Table
  const sortState = useMemo(() => {
    if (!sortBy) return []
    return [{ id: sortBy, desc: sortDesc }]
  }, [sortBy, sortDesc])

  // Server-side pagination and sorting (queries IndexedDB)
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.data, // Support for hierarchical data
    manualPagination: true, // Server-side style (queries IndexedDB)
    manualSorting: true, // Server-side style (queries IndexedDB)
    enableSorting: true,
    pageCount: Math.ceil(totalRows / pageSize),
    state: {
      pagination: {
        pageIndex,
        pageSize
      },
      sorting: sortState
    },
    onSortingChange: changeSorting
  })

  const classes = useMemo(() => ({
    table: '',
    header: '',
    cell: '',
    footer: '',
    ...(componentClasses || {})
  }), [componentClasses])

  const styles = useMemo(() => ({
    table: undefined,
    header: undefined,
    cell: undefined,
    footer: undefined,
    ...(componentStyles || {})
  }), [componentStyles])

  const pagingInfo = useMemo(() => {
    const start = (pageIndex * pageSize) + 1
    const calcEnd = ((pageIndex + 1) * pageSize)
    const end = calcEnd > totalRows ? totalRows : calcEnd

    return {
      start,
      end,
      total: totalRows,
      pageState: { pageIndex, pageSize }
    }
  }, [pageIndex, pageSize, totalRows])

  // Container styles for fixed height layout
  const containerStyle = useMemo(() => {
    if (!fixedHeight) return {}

    return {
      display: 'flex',
      flexDirection: 'column',
      height: fixedHeight,
      overflow: 'hidden'
    }
  }, [fixedHeight])

  const tableContainerStyle = useMemo(() => {
    if (!fixedHeight) return {}

    return {
      flex: '1 1 auto',
      overflow: 'auto',
      position: 'relative'
    }
  }, [fixedHeight])

  return (
    <Screen isFetching={isFetching}>
      <div
        className={clsx('opal-grid', clsx(componentClasses?.table), className, { 'og-fixed-height': !!fixedHeight })}
        style={{ ...style, ...containerStyle }}
      >
        <div className='og-container relative' style={tableContainerStyle}>
          <table>
            <thead className={clsx('og-thead', { 'og-sticky-header': !!fixedHeight })}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className='og-tr'>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={mapHeaderClasses('og-th', header, classes.header)}
                      style={mapHeaderStyles(styles.header, header)}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className={mapHeaderLabelClasses('og-th-label', header)}>
                        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                        {header.column.getIsSorted() ? (
                          <span>
                            <Icon name={mapSortIcon(header)} />
                          </span>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={clsx('og-tbody', { 'no-footer': !pagingInfo })}>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className={`og-tr og-tr-depth-${row.depth} og-expandable`}>
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      key={cell.id}
                      className={mapCellClasses('og-td', cell, classes.cell)}
                      style={mapCellStyles(styles.cell, cell)}
                    >
                      {(index === 0) ? (row.getCanExpand()) ? (
                        <div
                          className='og-expander clickable flex items-center' onClick={(e) => {
                            e.stopPropagation()
                            row.getToggleExpandedHandler()(e)
                          }}
                        >
                          <Icon additionalClasses={clsx('expand-icon', { expanded: row.getIsExpanded() })} name='chevronRight' customSize={16} />
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        <div className='og-expander og-lead'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {includeFooter && (
              <tfoot className={clsx('og-tfoot', { 'og-sticky-footer': !!fixedHeight })}>
                {table.getFooterGroups().map(footerGroup => (
                  <tr key={footerGroup.id} className='og-tr'>
                    {footerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className={mapFooterClasses('og-th', header, classes.footer)}
                        style={mapHeaderStyles(styles.footer, header)}
                      >
                        {flexRender(header.column.columnDef.footer, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        <div className={clsx({ 'og-sticky-paging': !!fixedHeight })}>
          <Paging
            pagingInfo={pagingInfo}
            canPrev={pageIndex > 0}
            canNext={pageIndex < Math.ceil(totalRows / pageSize) - 1}
            onPrev={() => goToPage(pageIndex - 1)}
            onNext={() => goToPage(pageIndex + 1)}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            pageSizeOptions={pageSizeOptions}
          />
        </div>
      </div>
      {fixedHeight && (
        <style>{`
          .og-fixed-height .og-container {
            /* Scrollable body */
          }
          
          .og-sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: var(--opal-surface-default, #fff);
          }
          
          .og-sticky-footer {
            position: sticky;
            bottom: 0;
            z-index: 10;
            background: var(--opal-surface-default, #fff);
          }
          
          .og-sticky-paging {
            flex-shrink: 0;
            background: var(--opal-surface-default, #fff);
            border-top: 1px solid var(--opal-border-default, #e0e0e0);
            padding-top: 0.5rem;
          }
        `}
        </style>
      )}
    </Screen>
  )
}

DDPReportIndexedDBGrid.propTypes = {
  /** Default sort state of the grid */
  defaultSort: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool
  })),

  /** Page size options for selector */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

  /** Fixed height for scrollable table (e.g., '600px', '80vh') */
  fixedHeight: PropTypes.string,

  className: PropTypes.string,
  style: PropTypes.object,
  componentClasses: PropTypes.shape({
    table: PropTypes.string,
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    table: PropTypes.object,
    header: PropTypes.object,
    cell: PropTypes.object,
    footer: PropTypes.object
  })
}

DDPReportIndexedDBGrid.defaultProps = {
  defaultSort: [],
  pageSizeOptions: [50, 100, 250, 500, 1000]
}

export default DDPReportIndexedDBGrid
